import {
  AccountRoster,
  Button,
  Dialog,
  ErrorText,
  revealDdAccountNumber,
  type AccountRosterActions,
  type DdAccountKind,
  type ListDdAccountsResponse,
} from '@dataroom/shared';
import {
  useCreateDdAccount,
  useDdAccounts,
  useMergeDdAccount,
  useUpdateDdAccount,
} from '@/api/hooks';

/** The kinds to show: one roster question's kind, the kind whose placeholder a
 * checklist item sits under, or (neither) every kind. */
function kindsFor(
  data: ListDdAccountsResponse,
  kind: DdAccountKind | null | undefined,
  placeholderTagId: string | null | undefined,
): DdAccountKind[] | undefined {
  if (kind) return [kind];
  if (placeholderTagId) {
    const match = data.kinds.filter((k) => k.placeholders.some((p) => p.tagId === placeholderTagId));
    if (match.length > 0) return match.map((k) => k.kind);
  }
  return undefined;
}

/**
 * The room's ACCOUNT ROSTER for the intermediary (ADR-0037): list, add, edit,
 * merge, and reveal a stored full number on an explicit click. Embedded under a
 * Financial roster question, and inside {@link DdAccountsDialog}.
 */
export function DdAccountsPanel({
  roomId,
  kind,
  placeholderTagId,
}: {
  roomId: string;
  kind?: DdAccountKind | null;
  placeholderTagId?: string | null;
}) {
  const accounts = useDdAccounts(roomId);
  const create = useCreateDdAccount(roomId);
  const update = useUpdateDdAccount(roomId);
  const merge = useMergeDdAccount(roomId);

  if (accounts.isPending) return <p className="text-sm text-muted-foreground">Loading accounts…</p>;
  if (accounts.error) return <ErrorText error={accounts.error} />;

  const actions: AccountRosterActions = {
    create: (body) => create.mutateAsync(body),
    update: (accountId, body) => update.mutateAsync({ accountId, body }),
    merge: (accountId, intoAccountId) => merge.mutateAsync({ accountId, intoAccountId }),
    reveal: (accountId) => revealDdAccountNumber(roomId, accountId),
  };
  return (
    <AccountRoster
      data={accounts.data}
      kinds={kindsFor(accounts.data, kind, placeholderTagId)}
      actions={actions}
    />
  );
}

/** The roster in a dialog — opened from the checklist ("Accounts" in the card
 * header, or "Manage accounts" on a placeholder item). */
export function DdAccountsDialog({
  roomId,
  placeholderTagId,
  onClose,
}: {
  roomId: string;
  placeholderTagId?: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      title="Accounts"
      description="Each account gets its own checklist category for its statements and documents. Only the last 4 digits of a number are shown."
      size="lg"
      onClose={onClose}
      footer={
        <Button variant="outline" className="ml-auto" onClick={onClose}>
          Done
        </Button>
      }
    >
      <DdAccountsPanel roomId={roomId} placeholderTagId={placeholderTagId} />
    </Dialog>
  );
}
